import { type CSSProperties } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PlusIcon } from '@huaca/ui';
import { newId } from '../ids';
import type { Condition, ConditionOperator, Field, FieldType, Section, TemplateBody } from '../types';
import { GripIcon, TrashIcon } from './icons';
import { TemplatePreview } from './TemplatePreview';

/**
 * The shared form-editing engine: sections/fields/conditions, the immutable reducers, drag-reorder,
 * and the live preview. It is deliberately presentational — it takes the whole `body` and an
 * `onChange`, and knows nothing about WHERE the body came from or how it is saved. Two wrappers give it
 * those: `TemplateBuilder` (master template CRUD, Editor-gated, firm-logo) and `SnapshotEditor` (one
 * onboarding's own snapshot). Extracting this — rather than mode-flagging one component — keeps the two
 * genuinely different save flows apart while sharing 100% of the editing surface.
 */

/**
 * Sensors shared by the section list and every section's field list. PointerSensor with a small
 * activation distance so a click on the grip that never moves still behaves as a click; the
 * KeyboardSensor makes the grip operable without a mouse (Space to pick up, arrows to move). The
 * arrow buttons remain as an explicit touch/keyboard fallback and are what the builder tests drive.
 */
function useReorderSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

/** Maps a dnd-kit drag end over an id list to (from, to) indices, then calls the reorder handler. */
function onSortEnd(ids: string[], apply: (from: number, to: number) => void) {
  return (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from !== -1 && to !== -1) apply(from, to);
  };
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'טקסט' },
  { value: 'number', label: 'מספר' },
  { value: 'date', label: 'תאריך' },
  { value: 'select', label: 'בחירה מרשימה' },
  { value: 'file', label: 'קובץ' },
];

const OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'שווה ל' },
  { value: 'is-one-of', label: 'אחד מהערכים' },
  { value: 'has-any-value', label: 'מולא (יש ערך כלשהו)' },
  { value: 'is-empty', label: 'ריק' },
];

/**
 * Move an item from one index to another — the reorder the drag grip performs. No-op for
 * out-of-range or same index. Exported so builder-rules.test can drive a reorder directly: dnd-kit's
 * pointer/keyboard drag cannot be simulated in jsdom (it needs real layout rects), so the test
 * exercises this production helper rather than a mimed drag.
 */
export function reorder<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  if (item === undefined) return arr;
  copy.splice(to, 0, item);
  return copy;
}

const newField = (order: number): Field => ({
  id: newId('fld'),
  label: '',
  type: 'text',
  required: false,
  order,
  visibleWhen: null,
});

/** The editing surface: an edit column (draggable sections → fields → conditions) + a live preview. */
export function FormBuilderBody({ body, onChange }: { body: TemplateBody; onChange: (body: TemplateBody) => void }) {
  const sensors = useReorderSensors();

  const setSection = (id: string, patch: Partial<Section>) =>
    onChange({ ...body, sections: body.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  const setField = (sectionId: string, fieldId: string, patch: Partial<Field>) =>
    onChange({
      ...body,
      sections: body.sections.map((s) =>
        s.id === sectionId ? { ...s, fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)) } : s,
      ),
    });

  const addSection = () =>
    onChange({
      ...body,
      sections: [
        ...body.sections,
        { id: newId('sec'), title: `מקטע ${body.sections.length + 1}`, order: body.sections.length, visibleWhen: null, fields: [] },
      ],
    });

  const removeSection = (id: string) =>
    onChange({ ...body, sections: body.sections.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })) });

  const reorderSections = (from: number, to: number) =>
    onChange({ ...body, sections: reorder(body.sections, from, to).map((s, i) => ({ ...s, order: i })) });

  const addField = (sectionId: string) =>
    onChange({
      ...body,
      sections: body.sections.map((s) =>
        s.id === sectionId ? { ...s, fields: [...s.fields, newField(s.fields.length)] } : s,
      ),
    });

  const removeField = (sectionId: string, fieldId: string) =>
    onChange({
      ...body,
      sections: body.sections.map((s) =>
        s.id === sectionId ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId).map((f, i) => ({ ...f, order: i })) } : s,
      ),
    });

  const reorderFields = (sectionId: string, from: number, to: number) =>
    onChange({
      ...body,
      sections: body.sections.map((s) =>
        s.id === sectionId ? { ...s, fields: reorder(s.fields, from, to).map((f, i) => ({ ...f, order: i })) } : s,
      ),
    });

  // All fields that appear strictly before a given section / field (valid condition sources).
  const fieldsBeforeSection = (sectionIdx: number): Field[] =>
    body.sections.slice(0, sectionIdx).flatMap((s) => s.fields);
  const fieldsBeforeField = (sectionIdx: number, fieldIdx: number): Field[] => [
    ...fieldsBeforeSection(sectionIdx),
    ...(body.sections[sectionIdx]?.fields.slice(0, fieldIdx) ?? []),
  ];

  return (
    <div className="claro-builder-grid">
      <div className="claro-builder-edit">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onSortEnd(
            body.sections.map((s) => s.id),
            reorderSections,
          )}
        >
          <SortableContext items={body.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {body.sections.map((section, sIdx) => (
              <SectionEditor
                key={section.id}
                section={section}
                sensors={sensors}
                earlierFields={fieldsBeforeSection(sIdx)}
                earlierFieldsForField={(fIdx) => fieldsBeforeField(sIdx, fIdx)}
                onChange={(patch) => setSection(section.id, patch)}
                onRemove={() => removeSection(section.id)}
                onAddField={() => addField(section.id)}
                onFieldChange={(fieldId, patch) => setField(section.id, fieldId, patch)}
                onFieldReorder={(from, to) => reorderFields(section.id, from, to)}
                onFieldRemove={(fieldId) => removeField(section.id, fieldId)}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button type="button" onClick={addSection} data-testid="add-section">
          <PlusIcon />
          הוספת מקטע
        </button>
      </div>

      <div className="claro-builder-preview">
        <h3>תצוגה מקדימה</h3>
        <p className="claro-hint">מלאו את השדות כדי לראות איך תנאים מציגים ומסתירים חלקים.</p>
        <TemplatePreview body={body} />
      </div>
    </div>
  );
}

function SectionEditor(props: {
  section: Section;
  sensors: ReturnType<typeof useReorderSensors>;
  earlierFields: Field[];
  earlierFieldsForField: (fieldIdx: number) => Field[];
  onChange: (patch: Partial<Section>) => void;
  onRemove: () => void;
  onAddField: () => void;
  onFieldChange: (fieldId: string, patch: Partial<Field>) => void;
  onFieldReorder: (from: number, to: number) => void;
  onFieldRemove: (fieldId: string) => void;
}) {
  const { section, earlierFields } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 2 : undefined,
  };

  return (
    <div className="claro-section" data-testid={`section-${section.id}`} ref={setNodeRef} style={style}>
      <div className="claro-section-head">
        <button type="button" className="claro-grip" aria-label="גרירה לסידור המקטע" {...attributes} {...listeners}>
          <GripIcon />
        </button>
        <input
          className="claro-section-title"
          value={section.title}
          onChange={(e) => props.onChange({ title: e.target.value })}
          placeholder="כותרת המקטע"
          aria-label="כותרת המקטע"
        />
        <button type="button" className="claro-icon-btn claro-icon-btn--danger" onClick={props.onRemove} aria-label="מחק מקטע">
          <TrashIcon />
        </button>
      </div>

      <ConditionEditor
        label="הצג מקטע זה רק אם…"
        condition={section.visibleWhen}
        earlierFields={earlierFields}
        onChange={(c) => props.onChange({ visibleWhen: c })}
      />

      <DndContext
        sensors={props.sensors}
        collisionDetection={closestCenter}
        onDragEnd={onSortEnd(
          section.fields.map((f) => f.id),
          props.onFieldReorder,
        )}
      >
        <SortableContext items={section.fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          {section.fields.map((field, fIdx) => (
            <FieldEditor
              key={field.id}
              field={field}
              earlierFields={props.earlierFieldsForField(fIdx)}
              onChange={(patch) => props.onFieldChange(field.id, patch)}
              onRemove={() => props.onFieldRemove(field.id)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <button type="button" className="claro-add-field" onClick={props.onAddField} data-testid={`add-field-${section.id}`}>
        <PlusIcon />
        הוספת שדה
      </button>
    </div>
  );
}

function FieldEditor(props: {
  field: Field;
  earlierFields: Field[];
  onChange: (patch: Partial<Field>) => void;
  onRemove: () => void;
}) {
  const { field, earlierFields } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  const setOptionAt = (i: number, text: string) => {
    const options = (field.options ?? []).map((o, oi) => (oi === i ? { value: text, label: text } : o));
    props.onChange({ options });
  };
  const addOption = () => props.onChange({ options: [...(field.options ?? []), { value: '', label: '' }] });
  const removeOption = (i: number) => props.onChange({ options: (field.options ?? []).filter((_, oi) => oi !== i) });

  return (
    <div className="claro-field" data-testid={`field-${field.id}`} ref={setNodeRef} style={style}>
      <div className="claro-field-row">
        <button type="button" className="claro-grip" aria-label="גרירה לסידור השדה" {...attributes} {...listeners}>
          <GripIcon />
        </button>
        <input
          className="claro-field-label"
          value={field.label}
          onChange={(e) => props.onChange({ label: e.target.value })}
          placeholder="תווית השדה"
          aria-label="תווית השדה"
        />
        <select
          value={field.type}
          onChange={(e) => props.onChange({ type: e.target.value as FieldType, options: e.target.value === 'select' ? field.options ?? [] : undefined })}
          aria-label="סוג השדה"
        >
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <label className="huaca-check">
          <input type="checkbox" checked={field.required} onChange={(e) => props.onChange({ required: e.target.checked })} />
          חובה
        </label>
        <button type="button" className="claro-icon-btn claro-icon-btn--danger" onClick={props.onRemove} aria-label="מחק שדה">
          <TrashIcon />
        </button>
      </div>

      {field.type === 'select' && (
        <div className="claro-options">
          <div className="claro-options-label">אפשרויות בחירה:</div>
          {(field.options ?? []).map((o, i) => (
            <div className="claro-option-row" key={i}>
              <input value={o.label} onChange={(e) => setOptionAt(i, e.target.value)} placeholder={`אפשרות ${i + 1}`} />
              <button type="button" className="btn-ghost" onClick={() => removeOption(i)} aria-label="מחק אפשרות">✕</button>
            </div>
          ))}
          <button type="button" className="btn-ghost" onClick={addOption}>
            <PlusIcon className="huaca-icon--sm" />
            אפשרות
          </button>
        </div>
      )}

      <ConditionEditor
        label="הצג שדה זה רק אם…"
        condition={field.visibleWhen}
        earlierFields={earlierFields}
        onChange={(c) => props.onChange({ visibleWhen: c })}
      />
    </div>
  );
}

function ConditionEditor(props: {
  label: string;
  condition: Condition | null;
  earlierFields: Field[];
  onChange: (c: Condition | null) => void;
}) {
  const { condition, earlierFields } = props;
  const on = condition !== null;
  const predicate = condition && condition.op === 'predicate' ? condition : null;
  const sourceField = earlierFields.find((f) => f.id === predicate?.field);

  const toggle = (checked: boolean) => {
    if (!checked) return props.onChange(null);
    const first = earlierFields[0];
    if (!first) return props.onChange(null);
    props.onChange({ op: 'predicate', field: first.id, operator: 'has-any-value' });
  };

  return (
    <div className="claro-condition">
      <label className="huaca-check">
        {/*
          Disabled only when there is nothing to turn ON. Gating purely on `earlierFields.length`
          trapped the author: reorder a field above its condition's source and the condition becomes
          invalid, but with no earlier fields left the checkbox went dead — so it could not be
          cleared or repointed, and save refused the template. The only ways out were deleting the
          field or abandoning the edit. An existing condition must always be removable.
        */}
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => toggle(e.target.checked)}
          disabled={earlierFields.length === 0 && !on}
        />
        {props.label}
      </label>
      {earlierFields.length === 0 && on === false && <span className="claro-hint"> (אין שדות קודמים להתניה)</span>}
      {earlierFields.length === 0 && on && (
        // The orphaned state: say so plainly, rather than leaving an empty source dropdown that
        // looks like a rendering bug.
        <span className="claro-hint"> (ההתניה מפנה לשדה שאינו קודם עוד — יש להסירה)</span>
      )}

      {on && predicate && (
        <div className="claro-condition-controls">
          <select
            value={predicate.field}
            onChange={(e) => props.onChange({ ...predicate, field: e.target.value, value: undefined })}
            aria-label="שדה מקור"
          >
            {earlierFields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label || '(ללא שם)'}
              </option>
            ))}
          </select>
          <select
            value={predicate.operator}
            onChange={(e) => {
              const op = e.target.value as ConditionOperator;
              const value = op === 'is-one-of' ? [] : op === 'equals' ? '' : undefined;
              props.onChange({ ...predicate, operator: op, value });
            }}
            aria-label="תנאי"
          >
            {OPERATORS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {/* equals → a single value (dropdown for select sources, else text). */}
          {predicate.operator === 'equals' &&
            (sourceField?.type === 'select' ? (
              <select
                value={String(predicate.value ?? '')}
                onChange={(e) => props.onChange({ ...predicate, value: e.target.value })}
                aria-label="ערך"
              >
                <option value="">— בחרו —</option>
                {(sourceField.options ?? []).map((o, i) => (
                  <option key={i} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={String(predicate.value ?? '')}
                onChange={(e) => props.onChange({ ...predicate, value: e.target.value })}
                placeholder="ערך"
                aria-label="ערך"
              />
            ))}

          {/* is-one-of → multiple values (checkboxes for select sources, else comma-separated). */}
          {predicate.operator === 'is-one-of' &&
            (sourceField?.type === 'select' ? (
              <div className="claro-multi">
                {(sourceField.options ?? []).map((o, i) => {
                  const arr = Array.isArray(predicate.value) ? (predicate.value as unknown[]).map(String) : [];
                  const checked = arr.includes(o.value);
                  return (
                    <label key={i} className="huaca-check">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          props.onChange({
                            ...predicate,
                            value: e.target.checked ? [...arr, o.value] : arr.filter((v) => v !== o.value),
                          })
                        }
                      />
                      {o.label || o.value}
                    </label>
                  );
                })}
              </div>
            ) : (
              <input
                value={Array.isArray(predicate.value) ? (predicate.value as unknown[]).map(String).join(', ') : ''}
                onChange={(e) =>
                  props.onChange({
                    ...predicate,
                    value: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="ערכים מופרדים בפסיק"
                aria-label="ערכים"
              />
            ))}
        </div>
      )}
    </div>
  );
}
